<template>
  <div ref="root" :class="rootClass">
    <transition
      :name="drawerAnimation"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @enter="onEnter"
    >
      <div
        v-if="isActive"
        :class="drawerClass"
        :aria-modal="ariaModal"
        role="dialog"
        class="ak-modal ak-modal--drawer"
        @click.self="clickClosesOutside && cancel('outside')"
      >
        <ak-form
          :submit="submitHandler"
          class="ak-modal__dialog ak-modal__dialog--drawer"
          :class="overflowShown ? 'overflow-visible' : ''"
          :style="[maxWidth, minWidth]"
        >
          <ak-close
            v-if="showX"
            class="ak-modal__close"
            @click.prevent="cancel('x')"
          />
          <div
            class="ak-modal__body"
            :class="{ 'ak-modal__body--paddingless': paddingless }"
          >
            <h3 v-if="title" class="color-primary">{{ title }}</h3>
            <component
              :is="component"
              v-if="component"
              v-bind="props"
              v-on="events"
              @close="close"
            />
            <div v-else-if="content" v-html="content" />
            <template v-else>
              <slot name="body" :close="close" />
            </template>
          </div>
          <div class="ak-modal__footer">
            <slot name="footer" :close="close" />
          </div>
        </ak-form>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { modalProps, drawerProps, modalMixin } from '../../mixins/modalMixin'
import Form from '../form/Form.vue'
import Close from '../close/Close.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-drawer />
   */
  name: 'AkDrawer',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Form.name]: Form,
    [Close.name]: Close,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...modalProps,
    ...drawerProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close', 'update:active'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes object for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const root = ref(null)

    const mixin = modalMixin({
      type: 'drawer',
      props,
      emit,
      attrs,
      refs: {
        root,
      },
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to apply to the dialog
     */
    const drawerClass = computed(() => {
      return {
        'ak-modal--has-container': props.container !== null,
        [`ak-modal--${props.position}`]: props.position,
        'ak-modal--inset': props.inset,
        [`ak-modal--inset-${props.inset}`]: props.inset,
        'ak-modal--has-backdrop': props.hasBackdrop && props.showOverlay,
        'overflow-visible': props.overflowShown,
        'z-index--top': props.showHighest,
      }
    })

    /**
     * @returns {Array}
     * @description Returns the animation depending on the position
     */
    const drawerAnimation = computed(() => {
      if (props.hasBackdrop) {
        return `slide-fade-out-${props.position}`
      }
      return `slide-${props.position}`
    })

    const maxWidth = computed(() => {
      let value
      if (Number.isInteger(props.width)) {
        value = `${props.width}px`
      } else {
        value = `${props.width}`
      }
      return {
        'max-width': value,
      }
    })

    const minWidth = computed(() => {
      let value
      if (Number.isInteger(props.minWidth)) {
        value = `${props.minWidth}px`
      } else {
        value = `${props.minWidth}`
      }
      return {
        'min-width': value,
      }
    })

    return {
      ...mixin,
      root,
      drawerClass,
      drawerAnimation,
      maxWidth,
      minWidth,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-modal--drawer {
  &__toggle {
    position: absolute;
    left: -1.5rem;
    top: 0.75rem;
    border-radius: 5px 0 0 5px;
    height: 2.5rem;
    width: 1.5rem;
    justify-content: center;
    align-items: center;
    display: inline-flex;
    background-color: #fff;
    box-shadow: $base-shadow;
  }
}
</style>
